/**
 * CallLogWizard — 3-step call documentation flow.
 *
 *   Step 1: Direction (Inbound | Outbound)
 *   Step 2: Type     (driven by /api/call-logs/types — 10 inbound / 11 outbound)
 *   Step 3: Type-specific extra fields + the universal common fields:
 *           - REASON FOR CALL textarea (required, placeholder copy spelled out)
 *           - Spoke with
 *           - Follow-up needed (Yes/No)
 *           - Status
 *           Type-specific extras are rendered from the schema returned by
 *           /types so we never have to hardcode a per-type form on the
 *           frontend.
 */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { PhoneIncoming, PhoneOutgoing, ArrowLeft, ArrowRight, X, CheckCircle2, Loader2, UserPlus, FileText, Search, Shield, MapPin, Phone as PhoneIcon, Mail, Clock, Building2 } from 'lucide-react';
import api from '../../services/api';
import { usDate } from '../../utils/usDate';
import MentionTextarea from '../common/MentionTextarea';
import { nowForDateTimeLocalInput } from '../../utils/labTime';
import DateTime24 from '../../components/common/DateTime24';

// Field keys that should render the patient picker instead of plain text.
const PATIENT_NAME_KEYS = new Set(['patient_name']);
// Field keys that should render the client picker.
const CLIENT_NAME_KEYS  = new Set(['client_name', 'facility']);
// Field keys that get auto-filled when a patient is picked (key → patient field)
const PATIENT_AUTOFILL  = { patient_dob: 'date_of_birth' };

const REASON_PLACEHOLDER =
  "Please put who you spoke with, what the call was about, and confirmation " +
  "of scheduling, pickup, supplies, requisition printouts, lab results, etc.";

export default function CallLogWizard({ onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [direction, setDirection] = useState(null); // 'inbound' | 'outbound'
  const [callType, setCallType]   = useState(null);
  const [typesCatalog, setTypesCatalog] = useState({ inbound: [], outbound: [], statuses: [] });
  const [details, setDetails] = useState({});
  const [spokeWith, setSpokeWith] = useState('');
  const [reason, setReason] = useState('');
  // Tag/mention companion for the REASON textarea — drives @user / #dept /
  // /f / /email / /o / /res autocomplete and ships those refs to the chat
  // mirror so dispatches (fax/email) actually fire when applicable.
  const [reasonMentions, setReasonMentions] = useState({ users: [], depts: [], entities: [] });
  const [followUp, setFollowUp] = useState(false);
  // Default the follow-up due time to tomorrow at 9am local — typical
  // call-back window. The user can edit it once Yes is chosen.
  const [followUpAt, setFollowUpAt] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(9, 0, 0, 0);
    // toISOString() here defeated the line above: 9am local became 16:00 UTC
    // and the field, which renders its string as literal wall clock, showed
    // 4pm. The helper formats the same instant as lab-local wall clock.
    return nowForDateTimeLocalInput(d);
  });
  // 3-state priority — derive is_stat from this for back-compat.
  const [priority, setPriority] = useState('ROUTINE');  // ROUTINE | STAT | ASAP
  const [status, setStatus] = useState('open');
  // Follow-up assignment — "me" (current user) or a department. Used by
  // the popup-alert poller in the app shell to decide who gets reminded.
  const [followUpAssignTo, setFollowUpAssignTo] = useState('me'); // me | department
  const [followUpDept, setFollowUpDept] = useState('Client Service');
  // Facility location details — site-level fields (Building / Door Code /
  // Building Schedule / Hours) are saved back to the client record so the
  // next call at the same client prefills. Patient-visit fields (Room/Bed/
  // Floor/Department/Isolation/Memory Care) stay on this call_log.
  const [facLoc, setFacLoc] = useState({
    building: '', floor: '', room: '', bed: '',
    department: '', isolation_unit: '', memory_care: false,
    door_code: '', building_schedule: '', hours: '',
  });
  // Extended detail for the picked patient/client cards. Fetched on pick
  // to render the rich-card view without changing the picker's basic shape.
  const [patientDetail, setPatientDetail] = useState(null);
  const [patientInsurances, setPatientInsurances] = useState([]);
  const [clientDetail, setClientDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  // Selected patient / client (full object). When set, the smart pickers
  // show the picked entity; details.patient_name / client_name still hold
  // the display string for backwards compat with existing reports.
  const [pickedPatient, setPickedPatient] = useState(null);
  const [pickedClient, setPickedClient]   = useState(null);
  const [pickedReq,    setPickedReq]      = useState(null);
  const [showAddPatient, setShowAddPatient] = useState(false);
  // Open-req match suggestions — fetched whenever the picked patient (or
  // client) changes. Each item carries a scheduled_at the user can attach.
  const [openReqMatches, setOpenReqMatches] = useState([]);
  const [dismissedReqIds, setDismissedReqIds] = useState(() => new Set());

  // Pull the catalog once. Drives steps 2 + 3 entirely from the server.
  useEffect(() => {
    api.get('/api/call-logs/types').then(r => setTypesCatalog(r.data || { inbound: [], outbound: [], statuses: [] }));
  }, []);

  // Patient detail + insurance fetch — drives the rich patient card
  // (MRN/DOB/Gender, Phone/Email, "Insurance verified" badge, Change link).
  useEffect(() => {
    if (!pickedPatient?.id) { setPatientDetail(null); setPatientInsurances([]); return; }
    setPatientDetail(pickedPatient);  // optimistic: use search-row data
    Promise.allSettled([
      api.get(`/patients/${pickedPatient.id}`),
      api.get(`/patient-tabs/${pickedPatient.id}/insurances`),
    ]).then(([detail, ins]) => {
      if (detail.status === 'fulfilled') setPatientDetail(detail.value.data);
      if (ins.status === 'fulfilled') setPatientInsurances(Array.isArray(ins.value.data) ? ins.value.data : []);
    });
  }, [pickedPatient?.id]);

  // Client detail + facility-info prefill. The facility-info endpoint
  // returns the stored building/door_code/schedule/hours so the user
  // doesn't retype them every call to the same client.
  useEffect(() => {
    if (!pickedClient?.client_id) { setClientDetail(null); return; }
    setClientDetail(pickedClient);  // optimistic
    api.get(`/api/v1/clients/${pickedClient.client_id}/facility-info`)
      .then(r => {
        const d = r.data || {};
        setClientDetail(prev => ({ ...(prev || {}), ...d }));
        const fl = d.facility_location || {};
        // Only prefill EMPTY fields — don't clobber what the user has typed.
        setFacLoc(prev => ({
          building:          prev.building          || fl.building          || '',
          floor:             prev.floor             || fl.floor             || '',
          room:              prev.room              || fl.room              || '',
          bed:               prev.bed               || fl.bed               || '',
          department:        prev.department        || fl.department        || '',
          isolation_unit:    prev.isolation_unit    || fl.isolation_unit    || '',
          memory_care:       prev.memory_care       || !!fl.memory_care,
          door_code:         prev.door_code         || fl.door_code         || '',
          building_schedule: prev.building_schedule || fl.building_schedule || '',
          hours:             prev.hours             || d.hours              || '',
        }));
      })
      .catch(() => { /* non-fatal — facility fields just stay empty */ });
  }, [pickedClient?.client_id]);

  // Look up ALL open reqs + orders for the picked patient (regardless of
  // client). The picked client is used only to SORT the list — entries
  // for the same client float to the top + get a "this client" tag.
  // We deliberately don't filter by client_id; the rep needs to see all
  // open work for the patient or they'll miss a call about a different
  // client's order. Skip when a req is already attached or patient cleared.
  useEffect(() => {
    if (!pickedPatient?.id || pickedReq) { setOpenReqMatches([]); return; }
    const ctrl = new AbortController();
    api.get('/api/call-logs/match-open-reqs', {
      params: { patient_id: pickedPatient.id },  // intentionally NO client_id
      signal: ctrl.signal,
    }).then(r => {
      const list = Array.isArray(r.data) ? r.data : [];
      const filtered = list.filter(x => !dismissedReqIds.has(x.id));
      // Sort: items for the currently-picked client first, then by created.
      const cid = pickedClient?.client_id;
      filtered.sort((a, b) => {
        const am = cid && String(a.client_id) === String(cid) ? 0 : 1;
        const bm = cid && String(b.client_id) === String(cid) ? 0 : 1;
        if (am !== bm) return am - bm;
        return (b.created_at || '').localeCompare(a.created_at || '');
      });
      setOpenReqMatches(filtered);
    }).catch(() => {});
    return () => ctrl.abort();
  }, [pickedPatient?.id, pickedClient?.client_id, pickedReq, dismissedReqIds]);

  const typeOptions = direction === 'inbound' ? typesCatalog.inbound : typesCatalog.outbound;
  const currentTypeDef = typeOptions.find(t => t.value === callType);
  const currentFields  = currentTypeDef?.fields || [];

  const goNext = () => setStep(s => Math.min(3, s + 1));
  const goPrev = () => setStep(s => Math.max(1, s - 1));

  const submit = async ({ thenCreateReq = false } = {}) => {
    if (!direction || !callType) { setErr('Direction and type are required.'); return; }
    if (!reason.trim()) { setErr('REASON FOR CALL is required.'); return; }
    setSaving(true); setErr('');
    try {
      // Merge facility-location into the call's details JSONB. The site-
      // level fields (building, door_code, building_schedule, hours) are
      // also pushed back to the client record below.
      const mergedDetails = { ...details, facility_location: { ...facLoc } };
      const res = await api.post('/api/call-logs/', {
        direction, call_type: callType,
        spoke_with: spokeWith,
        reason: reason.trim(),
        follow_up_needed: followUp,
        // Convert the datetime-local string ("2026-05-19T09:00") to an
        // ISO timestamp so the backend can store it as timestamptz.
        follow_up_at: followUp && followUpAt ? new Date(followUpAt).toISOString() : null,
        // Route the follow-up reminder to either the current user (default)
        // or a specific department so the right person gets the popup.
        assigned_to_user_id:    followUp && followUpAssignTo === 'me'         ? undefined : undefined,
        assigned_to_department: followUp && followUpAssignTo === 'department' ? followUpDept : undefined,
        priority,
        is_stat: priority === 'STAT',
        status,
        details: mergedDetails,
        related_patient_id: pickedPatient?.id ? String(pickedPatient.id) : undefined,
        related_client_id:  pickedClient?.client_id ? String(pickedClient.client_id) : undefined,
        // Attach an existing requisition/order so the call is linked back to
        // the order it was about (visible on the req's call-log tab).
        related_order_id:   pickedReq?.id ? String(pickedReq.id) : undefined,
      });

      // Save building-level defaults onto the client so the next call to
      // this facility prefills. Non-blocking — if the client wasn't picked
      // or the save fails, the call is still logged.
      if (pickedClient?.client_id) {
        api.patch(`/api/v1/clients/${pickedClient.client_id}/facility-info`, {
          facility_location: {
            building: facLoc.building, door_code: facLoc.door_code,
            building_schedule: facLoc.building_schedule,
            // Persist patient-visit defaults too — next call at the same
            // facility will pre-fill them but the user can override.
            department: facLoc.department, isolation_unit: facLoc.isolation_unit,
          },
          hours: facLoc.hours,
        }).catch(() => { /* non-blocking */ });
      }
      const savedCallId = res?.data?.id;
      // Save & Create Requisition handoff: redirect to the requisition
      // wizard with the patient prefilled + a call_log_id query param so
      // it can post back to /attach-requisition on save.
      if (thenCreateReq) {
        const params = new URLSearchParams();
        params.set('new', '1');
        if (pickedPatient?.id) params.set('patient_id', String(pickedPatient.id));
        if (savedCallId)        params.set('call_log_id', savedCallId);
        window.location.href = `/requisitions?${params.toString()}`;
        return;
      }
      // If the user @-mentioned, tagged a department, attached an entity,
      // OR added /f / /email destinations in the reason, mirror that to
      // a chat message so the dispatch hooks fire (notifications, fax,
      // email). Failures are silent — the call log is the source of truth.
      const totalMentions = (reasonMentions.users?.length || 0)
                          + (reasonMentions.depts?.length || 0)
                          + (reasonMentions.entities?.length || 0);
      if (totalMentions > 0) {
        try {
          const channel = reasonMentions.depts[0] || 'client_service';
          await api.post('/api/chat/messages', {
            channel,
            body: `📞 Call log — ${direction === 'inbound' ? 'Inbound' : 'Outbound'} · ${callType}\n${reason.trim()}`,
            mention_users: reasonMentions.users.length ? reasonMentions.users : undefined,
            mention_depts: reasonMentions.depts.length ? reasonMentions.depts : undefined,
            entities:      reasonMentions.entities.length
                             ? reasonMentions.entities.map(e => ({ type: e.type, id: e.id }))
                             : undefined,
          });
        } catch { /* swallow — call log is saved */ }
      }
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // Portal to <body> so the modal is never clipped by an ancestor that
  // creates a containing block (e.g. the header's backdrop-filter, a
  // `transform`, or a `filter`). Without this, opening the wizard from
  // inside <header> caused it to be cut off at the header's edge.
  return createPortal((
    <div onClick={onClose} style={overlay}>
      <div onClick={e => e.stopPropagation()} style={modal}>
        <div style={head}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Log a Call</h2>
          <button onClick={onClose} style={iconBtn}><X size={16} /></button>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 24px',
                      borderBottom: '1px solid var(--border-color, #e5e7eb)' }}>
          {[1, 2, 3].map(n => (
            <React.Fragment key={n}>
              <div style={{
                width: 28, height: 28, borderRadius: 14,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 12, fontWeight: 700,
                background: n <= step ? '#6366f1' : '#e5e7eb',
                color: n <= step ? '#fff' : '#9ca3af',
              }}>{n}</div>
              {n < 3 && <div style={{ flex: 1, height: 2, background: n < step ? '#6366f1' : '#e5e7eb' }} />}
            </React.Fragment>
          ))}
          <div style={{ marginLeft: 12, fontSize: 12, color: 'var(--text-secondary)' }}>
            {step === 1 ? 'Direction' : step === 2 ? 'Reason / Type' : 'Details'}
          </div>
        </div>

        <div style={{ padding: 24, flex: 1, overflowY: 'auto' }}>
          {/* Step 1 — Direction */}
          {step === 1 && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <DirectionCard active={direction === 'inbound'}
                onClick={() => { setDirection('inbound'); setCallType(null); goNext(); }}
                Icon={PhoneIncoming} title="Inbound Call" sub="Someone called us"
                color="#10b981" />
              <DirectionCard active={direction === 'outbound'}
                onClick={() => { setDirection('outbound'); setCallType(null); goNext(); }}
                Icon={PhoneOutgoing} title="Outbound Call" sub="We called someone"
                color="#0ea5e9" />
            </div>
          )}

          {/* Step 2 — Type / Reason */}
          {step === 2 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
              {typeOptions.map(t => (
                <button key={t.value} onClick={() => { setCallType(t.value); setDetails({}); goNext(); }}
                  style={{
                    padding: '14px 16px', borderRadius: 10, textAlign: 'left',
                    border: callType === t.value ? '2px solid #6366f1' : '1px solid var(--border-color, #e5e7eb)',
                    background: callType === t.value ? '#eef2ff' : 'var(--card-bg, #fff)',
                    cursor: 'pointer', fontWeight: 600, fontSize: 13,
                    color: callType === t.value ? '#6366f1' : 'var(--text-primary)',
                  }}>
                  {t.label}
                </button>
              ))}
              {typeOptions.length === 0 && <div style={{ padding: 20, color: '#94a3b8' }}>Loading types…</div>}
            </div>
          )}

          {/* Step 3 — Details */}
          {step === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ padding: '8px 12px', background: '#eef2ff', borderRadius: 8, fontSize: 12, color: '#6366f1', fontWeight: 600 }}>
                {direction === 'inbound' ? '📞 Inbound' : '📤 Outbound'} → {currentTypeDef?.label || callType}
              </div>

              {/* Type-specific extra fields. Split into two render passes
                  so the auto-match banner can sit between the patient/client
                  pickers and the scheduling fields — that way the rep sees
                  the open-req prompt BEFORE entering a redundant schedule. */}
              {/* Patient + Client cards (always first on step 3). */}
              {(() => {
                const visibleFields = currentFields
                  .filter(f => !(pickedPatient && Object.keys(PATIENT_AUTOFILL).includes(f.key)))
                  .filter(f => PATIENT_NAME_KEYS.has(f.key) || CLIENT_NAME_KEYS.has(f.key));
                if (visibleFields.length === 0) return null;
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                    {visibleFields.map(f => (
                      <DynamicField key={f.key} field={f}
                        value={details[f.key] || ''}
                        onChange={v => setDetails(d => ({ ...d, [f.key]: v }))}
                        pickedPatient={pickedPatient}
                        patientDetail={patientDetail}
                        patientInsurances={patientInsurances}
                        clientDetail={clientDetail}
                        onPickPatient={(p) => {
                          setPickedPatient(p);
                          setDetails(d => {
                            const next = { ...d, patient_name: p ? `${p.last_name || ''}, ${p.first_name || ''}`.trim() : '' };
                            for (const [key, srcField] of Object.entries(PATIENT_AUTOFILL)) {
                              if (currentFields.some(cf => cf.key === key)) {
                                next[key] = p ? (p[srcField] || '') : '';
                              }
                            }
                            return next;
                          });
                        }}
                        onClickAddPatient={() => setShowAddPatient(true)}
                        pickedClient={pickedClient}
                        onPickClient={(c) => {
                          setPickedClient(c);
                          setDetails(d => ({ ...d, [f.key]: c ? (c.dba_name || c.legal_name || '') : '' }));
                        }}
                      />
                    ))}
                  </div>
                );
              })()}

              {/* Once a req is attached, replace the banner with a compact
                  "✓ Attached" confirmation in the SAME spot so the rep
                  doesn't lose context. Previously the chip only appeared
                  in the manual picker at the bottom and the user had to
                  scroll to verify what they'd attached. */}
              {pickedReq && (
                <div style={{
                  border: '2px solid #22c55e',
                  background: 'rgba(34, 197, 94, 0.10)',
                  borderRadius: 10, padding: 12,
                  display: 'flex', alignItems: 'center', gap: 12,
                }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 28, height: 28, borderRadius: 14,
                    background: '#22c55e', color: '#fff', fontWeight: 800, fontSize: 14,
                  }}>✓</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: '#15803d' }}>
                      Attached: {pickedReq.requisition_number || pickedReq.order_number || `#${pickedReq.id}`}
                      {pickedReq.status && (
                        <span style={{ fontWeight: 500, color: 'var(--text-secondary)', fontSize: 11, marginLeft: 6 }}>
                          · {pickedReq.status}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>
                      {[pickedReq.patient_name, pickedReq.client_name,
                        pickedReq.scheduled_at && `📅 Scheduled ${String(pickedReq.scheduled_at).replace('T',' ')}`]
                          .filter(Boolean).join(' · ')}
                    </div>
                  </div>
                  <button type="button"
                    onClick={() => setPickedReq(null)}
                    style={{ padding: '6px 12px', borderRadius: 6,
                             border: '1px solid var(--border-color, #d1d5db)',
                             background: 'transparent', color: 'var(--text-secondary)',
                             fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    Change
                  </button>
                </div>
              )}

              {/* Auto-match: as soon as patient (+ optional client) is
                  picked, surface any open requisitions / orders so the
                  rep can attach one click. Sits right under the cards so
                  it's the first thing they see. Attaching also pulls the
                  scheduled appointment onto the call's follow-up timestamp. */}
              {openReqMatches.length > 0 && !pickedReq && (
                <div style={{
                  border: '2px solid #f59e0b',
                  background: 'rgba(251, 191, 36, 0.12)',
                  borderRadius: 10, padding: 14,
                  display: 'flex', flexDirection: 'column', gap: 10,
                }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#f59e0b' }}>
                    {(() => {
                      const reqCount = openReqMatches.filter(x => x.kind !== 'order').length;
                      const ordCount = openReqMatches.filter(x => x.kind === 'order').length;
                      const parts = [];
                      if (reqCount) parts.push(`${reqCount} open requisition${reqCount > 1 ? 's' : ''}`);
                      if (ordCount) parts.push(`${ordCount} open order${ordCount > 1 ? 's' : ''}`);
                      return `⚠ ${parts.join(' + ')} on file — is this call regarding one of them?`;
                    })()}
                  </div>
                  {openReqMatches.map(r => {
                    const isOrder = r.kind === 'order';
                    const label = isOrder
                      ? (r.order_number || `Order #${r.id}`)
                      : (r.requisition_number || `Req #${r.id}`);
                    const matchesPickedClient = pickedClient?.client_id
                      && String(r.client_id) === String(pickedClient.client_id);
                    return (
                    <div key={`${r.kind || 'req'}:${r.id}`} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      gap: 8, padding: '10px 12px', borderRadius: 8,
                      background: 'var(--card-bg, #fff)',
                      border: matchesPickedClient
                        ? '2px solid #22c55e'
                        : '1px solid var(--border-color, #e5e7eb)',
                    }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-primary)', flexWrap: 'wrap' }}>
                          <span style={{
                            display: 'inline-block', padding: '2px 6px', borderRadius: 4,
                            background: isOrder ? '#dbeafe' : '#ede9fe',
                            color: isOrder ? '#1d4ed8' : '#6d28d9',
                            fontSize: 9, fontWeight: 800, letterSpacing: 0.5,
                          }}>{isOrder ? 'ORDER' : 'REQ'}</span>
                          <span style={{ fontWeight: 700 }}>{label}</span>
                          <span style={{ fontWeight: 500, color: 'var(--text-secondary)', fontSize: 11 }}>· {r.status}</span>
                          {matchesPickedClient && (
                            <span style={{
                              padding: '1px 6px', borderRadius: 999,
                              background: '#dcfce7', color: '#15803d',
                              fontSize: 9, fontWeight: 800, letterSpacing: 0.3,
                            }}>● THIS CLIENT</span>
                          )}
                          {isOrder && r.requisition_number && (
                            <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>(from {r.requisition_number})</span>
                          )}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>
                          {[r.patient_name, r.client_name,
                            r.scheduled_at && `📅 Scheduled ${r.scheduled_at.replace('T',' ')}`]
                              .filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <button type="button"
                        onClick={() => {
                          setPickedReq({ ...r, requisition_number: label });
                          if (r.scheduled_at) {
                            setFollowUp(true);
                            setFollowUpAt(r.scheduled_at);
                          }
                        }}
                        style={{ padding: '8px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                                 background: '#6366f1', color: '#fff', fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap' }}>
                        Yes, attach
                      </button>
                      <button type="button"
                        onClick={() => setDismissedReqIds(prev => { const n = new Set(prev); n.add(r.id); return n; })}
                        title="Dismiss this suggestion"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}>
                        <X size={14} />
                      </button>
                    </div>
                    );
                  })}
                </div>
              )}

              {/* Pass 2: scheduling fields (scheduled_date / fasting_required,
                  etc.) — render AFTER the auto-match banner so the rep sees
                  the open-req prompt first. Two filters:
                  • Always drop `tests_ordered` — the "Save & Create
                    Requisition" button at the bottom is the canonical way
                    to set tests; a free-text field here would just
                    duplicate that and add no value.
                  • When the user attached an existing req/order
                    (pickedReq), drop the scheduling fields entirely —
                    the attached order already carries that info. */}
              {(() => {
                const SCHEDULING_KEYS = new Set(['scheduled_date', 'fasting_required']);
                const visibleFields = currentFields
                  .filter(f => !(pickedPatient && Object.keys(PATIENT_AUTOFILL).includes(f.key)))
                  .filter(f => !PATIENT_NAME_KEYS.has(f.key) && !CLIENT_NAME_KEYS.has(f.key))
                  .filter(f => f.key !== 'tests_ordered')
                  .filter(f => !(pickedReq && SCHEDULING_KEYS.has(f.key)));
                if (visibleFields.length === 0) return null;
                return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 12 }}>
                    {visibleFields.map(f => (
                      <DynamicField key={f.key} field={f}
                        value={details[f.key] || ''}
                        onChange={v => setDetails(d => ({ ...d, [f.key]: v }))}
                        pickedPatient={pickedPatient}
                        patientDetail={patientDetail}
                        patientInsurances={patientInsurances}
                        clientDetail={clientDetail}
                        onPickPatient={() => {}}
                        onClickAddPatient={() => {}}
                        pickedClient={pickedClient}
                        onPickClient={() => {}}
                      />
                    ))}
                  </div>
                );
              })()}

              <div>
                <label style={lb}>Spoke With</label>
                <input value={spokeWith} onChange={e => setSpokeWith(e.target.value)}
                  placeholder="Name + role of the person on the other end"
                  style={inp} />
              </div>

              <div>
                <label style={{ ...lb, fontWeight: 700, color: '#dc2626' }}>REASON FOR CALL *</label>
                <MentionTextarea
                  value={reason}
                  onChange={setReason}
                  mentions={reasonMentions}
                  setMentions={setReasonMentions}
                  placeholder={REASON_PLACEHOLDER}
                  style={{ ...inp, resize: 'vertical', fontFamily: 'inherit', minHeight: 140 }} />
              </div>

              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <label style={lb}>Follow-up Needed</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[{v:false,l:'No'},{v:true,l:'Yes'}].map(o => (
                      <button key={String(o.v)} onClick={() => setFollowUp(o.v)}
                        style={{ flex: 1, padding: '10px', borderRadius: 8,
                          border: followUp === o.v ? '2px solid #6366f1' : '1px solid var(--border-color, #d1d5db)',
                          background: followUp === o.v ? '#eef2ff' : 'transparent',
                          color: followUp === o.v ? '#6366f1' : 'var(--text-secondary)',
                          fontWeight: 600, fontSize: 13, cursor: 'pointer',
                        }}>{o.l}</button>
                    ))}
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <label style={lb}>Priority</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {[
                      { v: 'ROUTINE', bg: '#2563eb', fg: '#fff' },
                      { v: 'STAT',    bg: '#dc2626', fg: '#fff' },
                      { v: 'ASAP',    bg: '#f59e0b', fg: '#fff' },
                    ].map(o => {
                      const active = priority === o.v;
                      return (
                        <button key={o.v} type="button" onClick={() => setPriority(o.v)}
                          style={{ flex: 1, padding: '10px', borderRadius: 8,
                            border: '1px solid var(--border-color, #d1d5db)',
                            background: active ? o.bg : 'transparent',
                            color: active ? o.fg : 'var(--text-secondary)',
                            fontWeight: 700, fontSize: 12, cursor: 'pointer', letterSpacing: 0.5,
                          }}>{o.v}</button>
                      );
                    })}
                  </div>
                </div>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <label style={lb}>Status</label>
                  <select value={status} onChange={e => setStatus(e.target.value)} style={inp}>
                    {(typesCatalog.statuses || ['open','in_progress','follow_up','completed']).map(s =>
                      <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>
                    )}
                  </select>
                </div>
              </div>

              {/* Datetime picker — only when follow-up is actually needed.
                  Defaults to tomorrow 9am local; user can override. */}
              {followUp && (
                <div style={{ display: 'grid', gap: 10 }}>
                  <div>
                    <label style={lb}>Follow-up Date &amp; Time *</label>
                    <DateTime24
                      value={followUpAt}
                      onChange={v => setFollowUpAt(v)}
                      style={inp} />
                  </div>
                  {/* Who gets the popup reminder when this follow-up comes due.
                      "Me" assigns to the current user; "Department" routes to
                      anyone whose user.department matches the selected dept. */}
                  <div>
                    <label style={lb}>Remind</label>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                      {[{v:'me', lab:'Me'}, {v:'department', lab:'Department'}].map(o => {
                        const active = followUpAssignTo === o.v;
                        return (
                          <button key={o.v} onClick={() => setFollowUpAssignTo(o.v)}
                            style={{
                              flex: 1, padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                              border: active ? '2px solid #6366f1' : '1px solid var(--border-color, #d1d5db)',
                              background: active ? '#eef2ff' : 'transparent',
                              color: active ? '#6366f1' : 'var(--text-secondary)',
                              fontWeight: active ? 600 : 500, fontSize: 13,
                            }}>{o.lab}</button>
                        );
                      })}
                    </div>
                    {followUpAssignTo === 'department' && (
                      <select value={followUpDept}
                              onChange={e => setFollowUpDept(e.target.value)}
                              style={inp}>
                        {['Client Service','Billing','Phlebotomy','Lab','Accessioning',
                          'Dispatch','Sales','IT','Compliance','Management'].map(d =>
                          <option key={d} value={d}>{d}</option>)}
                      </select>
                    )}
                  </div>
                </div>
              )}

              {/* Manual picker — for when no auto-matches surface or the
                  user wants to attach a non-matching/historical req.
                  Suppressed once something IS attached — the "Attached"
                  chip up top is the canonical place to see/change it. */}
              {!pickedReq && (
                <div>
                  <label style={lb}>Attach Existing Requisition / Order</label>
                  <RequisitionPicker picked={pickedReq} onPick={(r) => {
                    setPickedReq(r);
                    if (r?.scheduled_at) {
                      setFollowUp(true);
                      setFollowUpAt(r.scheduled_at);
                    }
                  }} />
                </div>
              )}

              <FacilityLocationSection facLoc={facLoc} setFacLoc={setFacLoc}
                                       hasClient={!!pickedClient?.client_id} />

              {err && <div style={{ padding: '8px 12px', background: '#fee2e2', color: '#dc2626', borderRadius: 8, fontSize: 12 }}>{err}</div>}
            </div>
          )}
        </div>

        <div style={footer}>
          {step > 1 && <button onClick={goPrev} style={btnSecondary}><ArrowLeft size={13} /> Back</button>}
          <div style={{ flex: 1 }} />
          <button onClick={onClose} style={btnSecondary}>Cancel</button>
          {step === 3 ? (
            <>
              <button onClick={() => submit({ thenCreateReq: true })}
                      disabled={saving || !reason.trim() || !pickedPatient}
                      title={!pickedPatient ? 'Pick a patient first' : 'Save call + open requisition wizard for this patient'}
                      style={{ ...btnPrimary, background: '#0ea5e9', opacity: (saving || !reason.trim() || !pickedPatient) ? 0.5 : 1 }}>
                <FileText size={13} /> Save &amp; Create Requisition
              </button>
              <button onClick={() => submit()} disabled={saving || !reason.trim()}
                      style={{ ...btnPrimary, opacity: saving || !reason.trim() ? 0.5 : 1 }}>
                {saving ? <><Loader2 size={13} className="spin" style={{ animation: 'spin 1s linear infinite' }} /> Saving…</> :
                          <><CheckCircle2 size={13} /> Save Call</>}
              </button>
            </>
          ) : (
            <button onClick={goNext} disabled={(step === 1 && !direction) || (step === 2 && !callType)}
                   style={{ ...btnPrimary, opacity: (step === 1 && !direction) || (step === 2 && !callType) ? 0.5 : 1 }}>
              Next <ArrowRight size={13} />
            </button>
          )}
        </div>
      </div>

      {/* Inline Add Patient — lazy-loaded so we don't drag the full
          AddPatientModal into the bundle when the wizard isn't open. */}
      {showAddPatient && (
        <LazyAddPatient
          onClose={() => setShowAddPatient(false)}
          onAdded={(p) => {
            setShowAddPatient(false);
            // Reuse the picker selection path so DOB autofill etc. all fire.
            setPickedPatient(p);
            setDetails(d => {
              const next = { ...d, patient_name: `${p.last_name || ''}, ${p.first_name || ''}`.trim() };
              for (const [key, srcField] of Object.entries(PATIENT_AUTOFILL)) {
                if (currentFields.some(cf => cf.key === key)) {
                  next[key] = p[srcField] || '';
                }
              }
              return next;
            });
          }} />
      )}
    </div>
  ), document.body);
}

// Lazy wrapper for AddPatientModal — keeps the wizard's bundle slim.
const _AddPatientModalLazy = React.lazy(() => import('../../pages/lis/AddPatientModal'));
function LazyAddPatient({ onClose, onAdded }) {
  return (
    <React.Suspense fallback={null}>
      <_AddPatientModalLazy open onClose={onClose} onPatientAdded={onAdded} />
    </React.Suspense>
  );
}

function DirectionCard({ active, onClick, Icon, title, sub, color }) {
  return (
    <button onClick={onClick} style={{
      padding: '24px 18px', borderRadius: 14, textAlign: 'left',
      border: active ? `2px solid ${color}` : '1px solid var(--border-color, #e5e7eb)',
      background: active ? `${color}14` : 'var(--card-bg, #fff)',
      cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 6,
    }}>
      <Icon size={28} color={color} />
      <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{sub}</div>
    </button>
  );
}

function DynamicField({ field, value, onChange, pickedPatient, patientDetail, patientInsurances, onPickPatient, onClickAddPatient, pickedClient, clientDetail, onPickClient }) {
  const t = field.type || 'text';
  const props = { value, onChange: e => onChange(e.target?.value ?? e), style: inp };

  // Patient picker — typeahead w/ live search + Add Patient inline. When a
  // patient is selected, render a rich card (MRN/DOB/Gender + Insurance).
  // The picked card spans the full grid width so it doesn't get squeezed.
  if (PATIENT_NAME_KEYS.has(field.key)) {
    return (
      <div style={pickedPatient ? { gridColumn: '1 / -1' } : {}}>
        <label style={lb}>{field.label}{field.required && <span style={{ color: '#dc2626' }}> *</span>}</label>
        <PatientPicker picked={pickedPatient} detail={patientDetail} insurances={patientInsurances}
                       onPick={onPickPatient} onClickAdd={onClickAddPatient} />
      </div>
    );
  }
  // Client picker — rich card with address/phone/email/hours when picked.
  if (CLIENT_NAME_KEYS.has(field.key)) {
    return (
      <div style={pickedClient ? { gridColumn: '1 / -1' } : {}}>
        <label style={lb}>{field.label}{field.required && <span style={{ color: '#dc2626' }}> *</span>}</label>
        <ClientPicker picked={pickedClient} detail={clientDetail} onPick={onPickClient} />
      </div>
    );
  }

  return (
    <div>
      <label style={lb}>{field.label}{field.required && <span style={{ color: '#dc2626' }}> *</span>}</label>
      {t === 'textarea' ? <textarea rows={3} {...props} style={{ ...inp, resize: 'vertical' }} /> :
       t === 'date'     ? <input type="date" {...props} /> :
       /* 24-hour, like every other clock here — the native control draws
          08:15 PM on an en-US browser and no styling changes it. */
       t === 'datetime' ? <DateTime24 value={props.value}
                                      onChange={v => props.onChange({ target: { value: v } })}
                                      disabled={props.disabled} /> :
       t === 'number'   ? <input type="number" {...props} /> :
       t === 'yesno'    ? (
          <div style={{ display: 'flex', gap: 8 }}>
            {['Yes','No'].map(opt => (
              <button key={opt} type="button" onClick={() => onChange(opt)}
                style={{
                  flex: 1, padding: '8px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                  border: value === opt ? '2px solid #6366f1' : '1px solid var(--border-color, #d1d5db)',
                  background: value === opt ? '#eef2ff' : 'transparent',
                  color: value === opt ? '#6366f1' : 'var(--text-secondary)', cursor: 'pointer',
                }}>{opt}</button>
            ))}
          </div>
       ) :
       t === 'select'   ? (
          <select value={value} onChange={e => onChange(e.target.value)} style={inp}>
            <option value="">— Select —</option>
            {(field.options || []).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
       ) :
       <input type="text" {...props} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Generic live-search dropdown.
// ─────────────────────────────────────────────────────────────────────────────
function SearchPicker({ picked, onPick, placeholder, fetcher, displayFor, secondaryFor, addButton, renderPicked }) {
  const [q, setQ]           = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen]     = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (picked) return;             // hide search while a selection is shown
    const handle = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [picked]);

  useEffect(() => {
    if (picked) return;
    if (!q.trim()) { setResults([]); return; }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await fetcher(q.trim());
        setResults(Array.isArray(data) ? data.slice(0, 10) : []);
        setOpen(true);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q, picked, fetcher]);

  if (picked) {
    const clear = () => { onPick(null); setQ(''); setResults([]); };
    if (renderPicked) return renderPicked(picked, clear);
    return (
      <div style={{ ...inp, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 700, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayFor(picked)}</div>
          {secondaryFor && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{secondaryFor(picked)}</div>}
        </div>
        <button type="button" onClick={clear}
                title="Clear selection"
                style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#9ca3af', padding: 4 }}>
          <X size={14} />
        </button>
      </div>
    );
  }

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <Search size={13} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={placeholder}
               style={{ ...inp, paddingLeft: 30 }}
               onFocus={() => q && setOpen(true)} />
      </div>
      {open && (results.length > 0 || loading || addButton) && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'var(--card-bg, #fff)', border: '1px solid var(--border-color, #d1d5db)', borderRadius: 8, marginTop: 4, maxHeight: 280, overflowY: 'auto', boxShadow: '0 6px 16px rgba(0,0,0,0.15)', zIndex: 20 }}>
          {loading && <div style={{ padding: 10, fontSize: 12, color: '#9ca3af' }}>Searching…</div>}
          {!loading && results.length === 0 && <div style={{ padding: 10, fontSize: 12, color: '#9ca3af' }}>No matches</div>}
          {results.map((r, i) => (
            <button key={r.id || r.client_id || i} type="button"
                    onClick={() => { onPick(r); setOpen(false); setQ(''); }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '8px 12px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12, borderBottom: '1px solid var(--border-color, #f3f4f6)' }}>
              <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{displayFor(r)}</div>
              {secondaryFor && <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{secondaryFor(r)}</div>}
            </button>
          ))}
          {addButton}
        </div>
      )}
    </div>
  );
}

function initials(s) {
  return (s || '').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';
}

function PatientPicker({ picked, onPick, onClickAdd, detail, insurances }) {
  const fetcher = React.useCallback(async (q) => {
    const r = await api.get('/patients', { params: { search: q, limit: 10 } });
    return r.data;
  }, []);
  const p = detail || picked || {};
  const primaryIns = (insurances || []).find(i => /primary/i.test(String(i.insurance_type || '')))
                  || (insurances || [])[0];
  const insuranceVerified = !!(primaryIns?.eligibility_status === 'active'
                            || primaryIns?.is_verified || primaryIns?.verified);
  const fullName = `${p.first_name || ''} ${p.last_name || ''}`.trim()
                 || `${p.last_name || ''}, ${p.first_name || ''}`.replace(/^,\s*/, '').trim()
                 || '—';
  const renderCard = (_, clear) => (
    <div style={{
      border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 12,
      padding: 14, display: 'flex', gap: 12, alignItems: 'flex-start',
      background: 'var(--card-bg, transparent)',
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: 10, flexShrink: 0,
        background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
        color: '#fff', fontWeight: 800, fontSize: 16,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>{initials(fullName)}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--text-primary)' }}>{fullName}</div>
          {insuranceVerified && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              padding: '2px 8px', borderRadius: 999, background: '#dcfce7',
              color: '#15803d', fontSize: 11, fontWeight: 700,
            }}>
              <span style={{ width: 6, height: 6, borderRadius: 3, background: '#16a34a' }} />
              Insurance verified
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
          {p.mrn && <>#&nbsp;MRN: <strong style={{ color: 'var(--text-primary)' }}>{p.mrn}</strong>&nbsp;&nbsp;&nbsp;</>}
          {p.date_of_birth && <>DOB: <strong style={{ color: 'var(--text-primary)' }}>{usDate(p.date_of_birth)}</strong>&nbsp;&nbsp;&nbsp;</>}
          {(p.gender || p.sex) && <>Gender: <strong style={{ color: 'var(--text-primary)' }}>{p.gender || p.sex}</strong></>}
        </div>
        {(() => {
          // Address line — matches the New Order patient card style:
          // 📍 3089 North Harbor Boulevard · Santa Ana, CA 92703
          const streetParts = [p.address_line_1, p.address_line_2].filter(Boolean).join(', ');
          const cityLine = [p.city, p.state].filter(Boolean).join(', ');
          const fullAddr = [streetParts, [cityLine, p.postal_code || p.zip].filter(Boolean).join(' ')]
                             .filter(Boolean).join(' · ');
          return fullAddr ? (
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
              <MapPin size={12} style={{ color: '#dc2626' }} />
              <span>{fullAddr}</span>
            </div>
          ) : null;
        })()}
        {(p.phone || p.email) && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>
            {p.phone && <>Phone: <strong style={{ color: 'var(--text-primary)' }}>{p.phone}</strong>&nbsp;&nbsp;&nbsp;</>}
            {p.email && <>Email: <strong style={{ color: 'var(--text-primary)' }}>{p.email}</strong></>}
          </div>
        )}
        {primaryIns && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Shield size={12} />
            <span>
              {(primaryIns.payer_name || primaryIns.carrier || '—')}
              {primaryIns.plan_name && <> — {primaryIns.plan_name}</>}
              {primaryIns.policy_number && <> · Policy #{primaryIns.policy_number}</>}
              {primaryIns.group_number && <> · Group {primaryIns.group_number}</>}
            </span>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
        <button type="button" disabled
          title="Use the Patient page to edit insurance"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '6px 12px', borderRadius: 8, border: 'none',
            background: '#10b981', color: '#fff', fontWeight: 600, fontSize: 12,
            cursor: 'not-allowed', opacity: 0.85,
          }}>
          <Shield size={12} /> Add / Update Insurance
        </button>
        <button type="button" onClick={clear}
          style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                   color: '#6366f1', fontSize: 12, fontWeight: 600, padding: 0 }}>
          Change
        </button>
      </div>
    </div>
  );
  return (
    <SearchPicker
      picked={picked}
      onPick={onPick}
      placeholder="Search by name, DOB, MRN, phone, email…"
      fetcher={fetcher}
      displayFor={(p) => `${p.last_name || ''}, ${p.first_name || ''}`.trim() || '—'}
      secondaryFor={(p) => [p.date_of_birth, p.mrn && `MRN ${p.mrn}`].filter(Boolean).join(' · ')}
      renderPicked={renderCard}
      addButton={onClickAdd && (
        <button type="button" onClick={(e) => { e.stopPropagation(); onClickAdd(); }}
                style={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '10px', border: 'none', borderTop: '1px solid var(--border-color, #f3f4f6)', background: '#eef2ff', color: '#6366f1', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
          <UserPlus size={13} /> Add New Patient
        </button>
      )}
    />
  );
}

function ClientPicker({ picked, onPick, detail }) {
  const fetcher = React.useCallback(async (q) => {
    const r = await api.get('/api/v1/clients/', { params: { search: q, limit: 10 } });
    return r.data;
  }, []);
  const c = detail || picked || {};
  const name = c.dba_name || c.legal_name || c.client_name || '—';
  const addressLine = [c.address, c.city, c.state, c.zip_code].filter(Boolean).join(', ');
  const renderCard = (_, clear) => (
    <div style={{
      border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 12,
      padding: 14, display: 'flex', gap: 12, alignItems: 'flex-start',
      background: 'var(--card-bg, transparent)',
    }}>
      <div style={{
        width: 48, height: 48, borderRadius: 10, flexShrink: 0,
        background: 'linear-gradient(135deg, #0ea5e9, #6366f1)',
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}><Building2 size={22} /></div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 800, fontSize: 15, color: 'var(--text-primary)' }}>{name}</div>
        {addressLine && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
            <MapPin size={12} /> {addressLine}
          </div>
        )}
        <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3, display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          {c.phone && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><PhoneIcon size={12} /> {c.phone}</span>}
          {c.email && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Mail size={12} /> {c.email}</span>}
          {c.hours && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Clock size={12} /> {c.hours}</span>}
        </div>
      </div>
      <button type="button" onClick={clear}
        style={{ background: 'transparent', border: 'none', cursor: 'pointer',
                 color: '#6366f1', fontSize: 12, fontWeight: 600, padding: 0, flexShrink: 0 }}>
        Change
      </button>
    </div>
  );
  return (
    <SearchPicker
      picked={picked}
      onPick={onPick}
      placeholder="Search clients by name…"
      fetcher={fetcher}
      displayFor={(c) => c.dba_name || c.legal_name || '—'}
      secondaryFor={(c) => [c.npi && `NPI ${c.npi}`, c.city, c.state].filter(Boolean).join(' · ')}
      renderPicked={renderCard}
    />
  );
}

function RequisitionPicker({ picked, onPick }) {
  // Search across req numbers + patient names. Backend already does both —
  // a single `search` param matches `requisition_number ILIKE` OR
  // patient name/MRN.
  const fetcher = React.useCallback(async (q) => {
    const r = await api.get('/api/requisitions', { params: { search: q, limit: 10 } });
    return r.data;
  }, []);
  return (
    <SearchPicker
      picked={picked}
      onPick={onPick}
      placeholder="Search by REQ#, patient name, MRN…"
      fetcher={fetcher}
      displayFor={(r) => r.requisition_number || `#${r.id}`}
      secondaryFor={(r) => [r.patient_name, r.patient_mrn && `MRN ${r.patient_mrn}`, r.status].filter(Boolean).join(' · ')}
    />
  );
}

// ── Facility Location ──────────────────────────────────────────────────
// "Where the patient will be drawn." Required (at least one field) — the
// site-level subset (Building / Door Code / Building Schedule / Hours) is
// saved back to clients.facility_location so the next call prefills.
function FacilityLocationSection({ facLoc, setFacLoc, hasClient }) {
  const set = (k, v) => setFacLoc(prev => ({ ...prev, [k]: v }));
  const cell = { display: 'flex', flexDirection: 'column', gap: 4 };
  const smLb = { ...lb, fontSize: 11 };
  return (
    <div style={{
      border: '1px solid var(--border-color, #e5e7eb)', borderRadius: 10,
      padding: 14, display: 'flex', flexDirection: 'column', gap: 12,
      background: 'var(--card-bg, transparent)',
    }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text-primary)' }}>
          Facility Location
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
          {/* This said "required" and showed a red asterisk, but nothing in
              this wizard ever enforced it — the label was simply wrong. */}
          Where the patient will be drawn — building, floor, room or bed. Optional.
          {hasClient && ' Site-level fields are saved to the client record.'}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        <div style={cell}>
          <label style={smLb}>Building</label>
          <input value={facLoc.building} onChange={e => set('building', e.target.value)}
                 placeholder="e.g. A" style={inp} />
        </div>
        <div style={cell}>
          <label style={smLb}>Floor</label>
          <input value={facLoc.floor} onChange={e => set('floor', e.target.value)}
                 placeholder="e.g. 3" style={inp} />
        </div>
        <div style={cell}>
          <label style={smLb}>Room #</label>
          <input value={facLoc.room} onChange={e => set('room', e.target.value)}
                 placeholder="e.g. 312" style={inp} />
        </div>
        <div style={cell}>
          <label style={smLb}>Bed #</label>
          <input value={facLoc.bed} onChange={e => set('bed', e.target.value)}
                 placeholder="e.g. 312-A" style={inp} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1fr', gap: 10 }}>
        <div style={cell}>
          <label style={smLb}>Department</label>
          <input value={facLoc.department} onChange={e => set('department', e.target.value)}
                 placeholder="e.g. ICU, Med-Surg, Oncology" style={inp} />
        </div>
        <div style={cell}>
          <label style={smLb}>Isolation Unit</label>
          <input value={facLoc.isolation_unit} onChange={e => set('isolation_unit', e.target.value)}
                 placeholder="e.g. Contact, Droplet, Airborne" style={inp} />
        </div>
        <label style={{ ...cell, flexDirection: 'row', alignItems: 'center', gap: 8,
                        padding: '8px 12px', border: '1px solid var(--border-color, #d1d5db)',
                        borderRadius: 8, cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={!!facLoc.memory_care}
                 onChange={e => set('memory_care', e.target.checked)} />
          <span style={{ fontSize: 12, fontWeight: 600 }}>Memory Care</span>
        </label>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 10 }}>
        <div style={cell}>
          <label style={smLb}>Door Code</label>
          <input value={facLoc.door_code} onChange={e => set('door_code', e.target.value)}
                 placeholder="e.g. #4423" style={inp} />
        </div>
        <div style={cell}>
          <label style={smLb}>Building Schedule</label>
          <input value={facLoc.building_schedule} onChange={e => set('building_schedule', e.target.value)}
                 placeholder="e.g. Open 6am–6pm M–F · receiving dock side B" style={inp} />
        </div>
      </div>

      <div style={cell}>
        <label style={smLb}>Hours (operational)</label>
        <input value={facLoc.hours} onChange={e => set('hours', e.target.value)}
               placeholder="e.g. 24/7 nursing · phlebotomy window 6–10am"
               style={inp} />
      </div>
    </div>
  );
}

const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1700, padding: 16 };
const modal = { background: 'var(--card-bg, #fff)', borderRadius: 14, boxShadow: '0 25px 50px rgba(0,0,0,0.3)', width: '95vw', maxWidth: 920, maxHeight: '92vh', display: 'flex', flexDirection: 'column' };
const head = { padding: '16px 24px', borderBottom: '1px solid var(--border-color, #e5e7eb)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const footer = { padding: '14px 24px', borderTop: '1px solid var(--border-color, #e5e7eb)', display: 'flex', gap: 8 };
const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: 4 };
const btnPrimary = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', border: 'none', background: '#6366f1', color: '#fff', borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: 'pointer' };
const btnSecondary = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 14px', border: '1px solid var(--border-color, #e5e7eb)', background: 'transparent', color: 'var(--text-secondary)', borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: 'pointer' };
const lb = { display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.4 };
const inp = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-color, #d1d5db)', fontSize: 13, outline: 'none', boxSizing: 'border-box', background: 'var(--input-bg, #fff)', color: 'var(--text-primary)' };
